import * as Sentry from "@sentry/browser";
import { AxiosError, AxiosRequestConfig, AxiosResponse } from "axios";
import { CUR8_SERVER, CORE_API } from "gatsby-env-variables";

export const CLIENT_REQUEST_ID_HEADER = "x-client-request-id";

// The backend echoes its per-request id on every response; recording it on
// breadcrumbs lets a web Sentry event be matched to its CloudWatch log trail.
const REQUEST_ID_RESPONSE_HEADER = "x-request-id";

export const createClientRequestId = (): string =>
  `web-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

const FIRST_PARTY_HOSTS = [CUR8_SERVER, CORE_API].filter(
  (host): host is string => typeof host === "string" && host.length > 0
);

// Only stamp our correlation header on our own API; third-party hosts must
// not receive it.
export const isFirstPartyUrl = (url: string | undefined): boolean => {
  if (!url) return false;
  if (url.startsWith("/")) return true;
  return FIRST_PARTY_HOSTS.some((host) => url.startsWith(host));
};

const requestStartTimes = new WeakMap<object, number>();

export const prepareRequestCorrelation = <T extends AxiosRequestConfig>(
  config: T
): T => {
  if (!isFirstPartyUrl(config.url)) return config;

  requestStartTimes.set(config, Date.now());
  config.headers = config.headers ?? {};
  config.headers[CLIENT_REQUEST_ID_HEADER] = createClientRequestId();
  return config;
};

const stripQueryAndHash = (url: string): string => url.split(/[?#]/)[0] ?? url;

// Path segments that are ids (numeric, Mongo, UUID, opaque tokens) collapse
// to :id so every request to the same endpoint shares one operation name.
const normalizeUrlSegment = (segment: string): string => {
  if (!segment) return segment;
  if (/^\d+$/.test(segment)) return ":id";
  if (/^[0-9a-f]{24}$/i.test(segment)) return ":id";
  if (
    /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
      segment
    )
  ) {
    return ":id";
  }
  if (/^[A-Za-z0-9_-]{18,}$/.test(segment) && /\d/.test(segment)) return ":id";

  return segment;
};

export const normalizeHttpUrl = (url?: string): string | undefined => {
  if (!url) return undefined;

  const path = stripQueryAndHash(url).replace(/^https?:\/\/[^/]+/i, "");
  const normalizedPath = path
    .split("/")
    .map(normalizeUrlSegment)
    .join("/")
    .replace(/\/{2,}/g, "/");

  return normalizedPath || "/";
};

const getHttpOperation = (
  config: AxiosRequestConfig | undefined
): string | undefined => {
  const method = config?.method?.toLowerCase();
  const url = normalizeHttpUrl(config?.url);
  if (!method && !url) return undefined;

  return [method, url].filter(Boolean).join(" ");
};

// 5xx statuses collapse into one bucket: a 500, 502, and 503 from the same
// endpoint are one server-side failure story, and the exact status is still
// searchable via the status_code tag. Distinct 4xx statuses stay separate
// because they indicate different client bugs.
const fingerprintStatus = (status: number | undefined): string => {
  if (status === undefined) return "no_status";
  if (status >= 500) return "5xx";
  return String(status);
};

export const getAxiosErrorTags = (
  error: AxiosError
): Record<string, string | undefined> => {
  const status = error.response?.status;

  return {
    status_code: status === undefined ? undefined : String(status),
    api_operation: getHttpOperation(error.config),
  };
};

// Mirrors the mobile fingerprint shape ("mobile, api, ...") so a generic
// "AxiosError: status 500" splits into one issue per endpoint instead of
// grouping every API failure under the shared axios constructor frame.
export const getAxiosErrorFingerprint = (error: AxiosError): string[] => [
  "web",
  "api",
  error.config?.method?.toUpperCase() ?? "UNKNOWN",
  getHttpOperation(error.config) ?? "unknown_operation",
  fingerprintStatus(error.response?.status),
];

export const getCorrelationIds = (
  config: AxiosRequestConfig | undefined,
  response: AxiosResponse | undefined
): Record<string, string | undefined> => ({
  client_request_id: config?.headers?.[CLIENT_REQUEST_ID_HEADER] as
    | string
    | undefined,
  request_id: response?.headers?.[REQUEST_ID_RESPONSE_HEADER] as
    | string
    | undefined,
});

const breadcrumbData = (
  config: AxiosRequestConfig | undefined,
  response: AxiosResponse | undefined
): Record<string, unknown> => {
  const durationStart = config ? requestStartTimes.get(config) : undefined;
  return {
    method: config?.method?.toUpperCase(),
    url: config?.url,
    status_code: response?.status,
    ...(durationStart ? { duration_ms: Date.now() - durationStart } : {}),
    ...getCorrelationIds(config, response),
  };
};

export const addHttpResponseBreadcrumb = (
  response: AxiosResponse
): AxiosResponse => {
  if (isFirstPartyUrl(response.config?.url)) {
    Sentry.addBreadcrumb({
      category: "http",
      type: "http",
      level: "info",
      data: breadcrumbData(response.config, response),
    });
  }
  return response;
};

export const addHttpErrorBreadcrumb = (error: AxiosError): void => {
  if (!isFirstPartyUrl(error.config?.url)) return;
  Sentry.addBreadcrumb({
    category: "http",
    type: "http",
    level: "error",
    data: {
      ...breadcrumbData(error.config, error.response),
      ...(error.code ? { code: error.code } : {}),
    },
  });
};
